import { authToken } from '../storage/authToken';
import * as Breadcrumbs from './breadcrumbs';

/**
 * Reports a runtime error to the OATRx backend (/api/frontend-incidents), which
 * de-dupes, PHI-scrubs and forwards a summary to the shared #oatrx-errors Slack
 * channel. Reporting must NEVER break the app — every path is guarded.
 */

const APP_SOURCE = 'driver-app';
const DEDUPE_WINDOW_MS = 15000;
const dedupeCache = new Map();

let incidentContext = {};

function trimString(value, maxLength = 4000) {
	if (typeof value !== 'string') {
		return value ?? null;
	}
	return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}

function safeJson(value) {
	if (value == null) {
		return null;
	}
	try {
		return JSON.parse(JSON.stringify(value));
	} catch (error) {
		return { serialization_error: error?.message || 'Unable to serialize payload' };
	}
}

export function setFrontendIncidentContext(context = {}) {
	incidentContext = { ...context, metadata: safeJson(context.metadata || null) };
}

function monitoringUrl() {
	const base = process.env.REACT_APP_BASE_URL || '/api/';
	return `${base.replace(/\/+$/, '')}/frontend-incidents`;
}

// The backend raises one Slack alert per new fingerprint, so the fingerprint uses the
// path without its query or hash: on /e-sign?id=<patient> the id changed for every
// patient and turned one error into one alert per patient. route_path keeps the full URL.
function pathOnly(routePath) {
	return typeof routePath === 'string' ? routePath.split(/[?#]/)[0] : '';
}

export function fingerprintOf(payload) {
	return [
		APP_SOURCE,
		payload.type || 'unknown',
		pathOnly(payload.route_path) || (typeof window !== 'undefined' ? window.location.pathname : ''),
		payload.message || 'no-message',
	].join('::');
}

function recentlySent(fingerprint) {
	const now = Date.now();
	const previous = dedupeCache.get(fingerprint);
	if (previous && now - previous < DEDUPE_WINDOW_MS) {
		return true;
	}
	dedupeCache.set(fingerprint, now);
	return false;
}

function normalize(payload) {
	const routePath = typeof window !== 'undefined'
		? `${window.location.pathname}${window.location.search}${window.location.hash}`
		: null;

	return {
		source: APP_SOURCE,
		type: trimString(payload.type || 'frontend_error', 120),
		severity: trimString(payload.severity || 'error', 40),
		message: trimString(payload.message || 'Unknown frontend error'),
		stack: trimString(payload.stack || null, 20000),
		component_stack: trimString(payload.component_stack || null, 20000),
		page_url: trimString(typeof window !== 'undefined' ? window.location.href : null, 2000),
		route_path: trimString(payload.route_path || routePath, 1000),
		build_version: trimString(payload.build_version || process.env.REACT_APP_BUILD_VERSION || null, 255),
		environment: trimString(process.env.NODE_ENV || 'development', 32),
		user_agent: trimString(typeof navigator !== 'undefined' ? navigator.userAgent : null, 2000),
		module_name: trimString(payload.module_name || null, 255),
		metadata: safeJson(payload.metadata || null),
		// How the user got here — labels only, see ./breadcrumbs.
		breadcrumbs: Breadcrumbs.snapshot(),
		occurred_at: payload.occurred_at || new Date().toISOString(),
	};
}

export default function reportFrontendIncident(payload = {}) {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		const merged = {
			...incidentContext,
			...payload,
			metadata: { ...(incidentContext.metadata || {}), ...(payload.metadata || {}) },
		};
		const normalized = normalize(merged);
		const fingerprint = fingerprintOf(normalized);
		if (recentlySent(fingerprint)) {
			return;
		}

		const token = authToken();
		fetch(monitoringUrl(), {
			method: 'POST',
			keepalive: true,
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
			body: JSON.stringify({ ...normalized, fingerprint }),
		}).catch(() => {});
	} catch (_) {
		// Reporting must never break the application.
	}
}
